import { Input, Node, Output } from "rete"
import { NodeData, WorkerInputs, WorkerOutputs } from "rete/types/core/data"
import { BooleanTileGrid, CategoricalTileGrid } from "../tile_grid"
import { booleanDataSocket, categoricalDataSocket } from "../socket_types"
import { BaseComponent } from "./base_component"
import { getCategoricalLabelsFromInputChain } from "./categorical_label_chain"
import {
  isCategoricalChainSeedingSuppressed,
  isLabelOutputReconnectInProgressForNode,
  mergeLabelOutputWireSnapshotsForReconnect,
  type LabelOutputWireSnapshot,
  reconnectLabelOutputsByNormalizedLabel,
  rememberLabelOutputWireSnapshots,
  runWithLabelOutputReconnect,
  snapshotLabelOutputConnections,
  takePendingLabelOutputWireSnapshots,
} from "./categorical_dynamic_io_connections"

export class CategoricalDifferenceComponent extends BaseComponent {
  private static connectionHooksByNodeId = new Set<number>()

  constructor() {
    super("Categorical difference")
    this.category = "Set operations"
    this.contextMenuName = "Categorical difference"
    this.description =
      "Compares two Categorical datasets (A and B). Outputs B's category only where the cell differs from A, " +
      "plus a Boolean mask over all changed cells and a Boolean output per class in B."
  }

  async builder(node: Node) {
    node.addInput(new Input("a", "A", categoricalDataSocket))
    node.addInput(new Input("b", "B", categoricalDataSocket))
    node.addOutput(new Output("out", "Changed", categoricalDataSocket))
    node.addOutput(new Output("All", "Mask", booleanDataSocket))

    const savedLabels = node.data.differenceLabels as Array<{ value: number; label: string }> | undefined
    const hasSavedLabels = Array.isArray(savedLabels) && savedLabels.length > 0
    if (hasSavedLabels) {
      this.syncBooleanOutputs(node, savedLabels!.map(({ value, label }) => [value, label]))
    } else {
      this.seedOutputsFromConnections(node)
    }

    if (this.editor && !CategoricalDifferenceComponent.connectionHooksByNodeId.has(node.id)) {
      CategoricalDifferenceComponent.connectionHooksByNodeId.add(node.id)
      const refreshOnConnectionChange = () => {
        if (isCategoricalChainSeedingSuppressed()) return
        this.seedOutputsFromConnections(node)
      }

      this.editor.on("connectioncreated", refreshOnConnectionChange)
      this.editor.on("connectionremoved", refreshOnConnectionChange)
      this.editor.on("noderemoved", (removedNode: any) => {
        if (removedNode?.id === node.id) {
          CategoricalDifferenceComponent.connectionHooksByNodeId.delete(node.id)
        }
      })
    }
  }

  /** Called after connections settle so upstream categorical labels are available. */
  refreshDynamicLabelOutputs(node: Node): void {
    this.seedOutputsFromConnections(node)
  }

  private syncBooleanOutputs(node: Node, labels: Array<[number, string]>, snapsOverride?: LabelOutputWireSnapshot[]) {
    const snaps =
      snapsOverride !== undefined
        ? snapsOverride
        : this.editor != null
          ? snapshotLabelOutputConnections(node, k => k.startsWith("label_"))
          : []

    const getExistingOutput = (key: string) => {
      const outputsAny = node.outputs as any
      if (typeof outputsAny?.get === "function") return outputsAny.get(key)
      return outputsAny?.[key]
    }

    const desired = new Map<string, string>()
    labels
      .filter(([value]) => value !== 255)
      .sort((a, b) => a[0] - b[0])
      .forEach(([value, label]) => {
        desired.set(`label_${value}`, label)
      })

    Array.from(node.outputs.values()).forEach((output: any) => {
      if (!String(output.key).startsWith("label_")) return
      if (desired.has(output.key)) return
      ;[...output.connections].forEach(c => this.editor?.removeConnection(c))
      node.removeOutput(output)
    })

    desired.forEach((label, key) => {
      const existing = getExistingOutput(key)
      if (existing) {
        existing.name = label
      } else {
        node.addOutput(new Output(key, label, booleanDataSocket))
      }
    })

    if (this.editor) {
      runWithLabelOutputReconnect(node, () =>
        reconnectLabelOutputsByNormalizedLabel(this.editor!, node, snaps, k => k.startsWith("label_"))
      )
    }
  }

  private pruneOutputsWithoutLabels(node: Node) {
    if (this.editor) {
      rememberLabelOutputWireSnapshots(
        node,
        snapshotLabelOutputConnections(node, k => k.startsWith("label_"))
      )
    }
    Array.from(node.outputs.values()).forEach((output: any) => {
      if (!String(output.key).startsWith("label_")) return
      ;[...output.connections].forEach(c => this.editor?.removeConnection(c))
      node.removeOutput(output)
    })
  }

  private seedOutputsFromConnections(node: Node) {
    if (!this.editor) return
    if (isLabelOutputReconnectInProgressForNode(node.id)) return

    const inputHasWire = (key: string) => {
      const inp = (node.inputs as any).get?.(key) ?? (node.inputs as any)[key]
      return Array.isArray(inp?.connections) && inp.connections.length > 0
    }

    const rows = getCategoricalLabelsFromInputChain(this.editor, node, "b")
    if (rows.length === 0) {
      if (!inputHasWire("a") && !inputHasWire("b")) {
        node.update()
        return
      }
      // B may be wired before upstream labels finish loading (Load Dataset, Paintbrush, etc.).
      if (inputHasWire("b")) {
        const hadLabels =
          Array.isArray(node.data.differenceLabels) && node.data.differenceLabels.length > 0
        if (hadLabels) {
          delete node.data.differenceLabels
          this.pruneOutputsWithoutLabels(node)
        }
        node.update()
        return
      }
      delete node.data.differenceLabels
      this.pruneOutputsWithoutLabels(node)
      node.update()
      return
    }

    node.data.differenceLabels = rows
    const pending = takePendingLabelOutputWireSnapshots(node)
    const live =
      this.editor != null ? snapshotLabelOutputConnections(node, k => k.startsWith("label_")) : []
    const merged = mergeLabelOutputWireSnapshotsForReconnect(live, pending)
    this.syncBooleanOutputs(node, rows.map(({ value, label }) => [value, label]), merged)
    node.update()
  }

  async worker(node: NodeData, inputs: WorkerInputs, outputs: WorkerOutputs) {
    const editorNode = this.editor?.nodes.find(n => n.id === node.id)
    if (editorNode === undefined) return

    const aInput = inputs["a"]?.[0]
    const bInput = inputs["b"]?.[0]
    if (!(aInput instanceof CategoricalTileGrid) || !(bInput instanceof CategoricalTileGrid)) {
      editorNode.meta.errorMessage = "Requires A and B categorical inputs"
      return
    }
    delete editorNode.meta.errorMessage

    const output = new CategoricalTileGrid(aInput.zoom, aInput.x, aInput.y, aInput.width, aInput.height)
    if (bInput.labels?.size) {
      output.setLabels(new Map(bInput.labels))
    }

    const allChanged = new BooleanTileGrid(aInput.zoom, aInput.x, aInput.y, aInput.width, aInput.height, false)

    const labelEntries = Array.from(bInput.labels?.entries() ?? [])
      .filter(([value]) => value !== 255)
      .sort((a, b) => a[0] - b[0])

    const boolByValue = new Map<number, BooleanTileGrid>()
    labelEntries.forEach(([value, label]) => {
      boolByValue.set(value, new BooleanTileGrid(aInput.zoom, aInput.x, aInput.y, aInput.width, aInput.height, false))
    })

    aInput.iterate((x, y, aVal) => {
      const bVal = bInput.get(x, y, aInput.zoom)
      if (aVal === bVal) {
        output.set(x, y, 255)
        return
      }

      output.set(x, y, bVal)
      allChanged.set(x, y, true)

      const classBool = boolByValue.get(bVal)
      if (classBool) {
        classBool.set(x, y, true)
      }
    })

    output.name = (editorNode.data.name as string) || "Categorical difference"
    allChanged.name = "Mask"
    outputs["out"] = output
    outputs["All"] = allChanged

    editorNode.data.differenceLabels = labelEntries.map(([value, label]) => ({ value, label }))
    this.syncBooleanOutputs(editorNode, labelEntries)

    labelEntries.forEach(([value, label]) => {
      const outputKey = `label_${value}`
      const labelOutput = (editorNode.outputs as any).get?.(outputKey) ?? (editorNode.outputs as any)[outputKey]
      if (!labelOutput) return
      const boolGrid = boolByValue.get(value)
      if (!boolGrid) return
      boolGrid.name = label
      outputs[outputKey] = boolGrid
    })

    editorNode.update()
  }
}
